import { Component, Element, Host, State, Listen, h, Prop } from '@stencil/core';
import { FacetChangeEvent, DisplayDataUpdateEvent, DisplayDataClearEvent, logDebug, getParameter } from '@hpfc/fc-frontend-core';

/**
 * Receives the `fccdDisplayDataUpdateEvent` with a list of data, and uses that information to determine what content to render.
 *
 * List content is passed in via the component's slot.
 *
 * Conditionally displays either: no results text, loading spinner, or slot content.
 *
 * @slot noContentMessage - Displayed when no result content
 * @slot errorMessage - Displayed when an error occurs
 */
@Component({
  tag: 'fccd-list-container',
  shadow: true, // Required to hide the <slot /> content when replacing with a loading spinner.
})
export class ListContainer {
  /**
   * Path to the list off of the fccdDisplayDataUpdateEvent.data event to display.
   */
  @Prop() dataPath: string = 'data';

  /**
   * Text to display under the loading spinner.
   */
  @Prop() loadingText: string = 'Loading';

  /**
   * Text to display when event data is empty.
   */
  @Prop() noContentText: string = 'No results found.';

  /**
   * Text to display when an error occurs.
   */
  @Prop() errorText: string = 'An error occurred gathering data.';

  /**
   * Keep track of the state of data to determine what content to display.
   *
   * `null` - loading spinner
   * empty array - no results found copy
   * populated array - slot content
   */
  @State() results: any[];

  /**
   * Reports when an error occurred.
   */
  @State() inErrorState: boolean = false;

  /**
   * Allows for custom JSX message for no content.
   */
  @State() hasNoContentSlot: boolean = false;

  /**
   * Allows for custom JSX message for when an error occurs.
   */
  @State() hasErrorSlot: boolean = false;

  /**
   * Listen for changes in the data to update the state.
   */
  @Listen('fccdDisplayDataUpdateEvent', { target: 'document' })
  updateHandler(event: CustomEvent) {
    const dataUpdateEvent = event?.detail as DisplayDataUpdateEvent;

    if (dataUpdateEvent?.data?.isError === true || dataUpdateEvent?.isError === true) {
      this.inErrorState = true;
    } else {
      const newResults = getParameter(dataUpdateEvent.data, this.dataPath);
      if (newResults) {
        this.results = newResults;
      }
      this.inErrorState = false;
    }
  }

  /**
   * Notification to clear data and trigger spinner.
   */
  @Listen('fccdDisplayDataClearEvent', { target: 'document' })
  clearHandler(event: CustomEvent) {
    logDebug('clearHandler', event);
    const clearEvent = event?.detail as DisplayDataClearEvent;

    if (clearEvent?.isError === true) {
      this.inErrorState = true;
    } else {
      this.results = null;
      this.inErrorState = false;
    }
  }

  /**
   * Keep track of when facets are changing, so a loading spinner can be displayed as new results are being fetched.
   */
  @Listen('fccdFacetChangeEvent', { target: 'document' })
  selectHandler(event: CustomEvent) {
    if (event?.detail instanceof FacetChangeEvent && event.detail.facetItem) {
      this.results = null;
    }
  }

  @Element() host: HTMLElement;

  componentWillLoad() {
    this.hasNoContentSlot = !!this.host.querySelector("[slot='noContentMessage']");
    this.hasErrorSlot = !!this.host.querySelector("[slot='errorMessage']");
  }

  render = () => {
    if (this.inErrorState) {
      return this.hasErrorSlot ? (
        <Host>
          <slot name="errorMessage" />
        </Host>
      ) : (
        <Host>
          <hp5-heading tag-selection="h3">{this.errorText}</hp5-heading>
        </Host>
      );
    } else if (!this.results) {
      return (
        <Host>
          <hp5-loading-panel loading-spinner-size="md" message={this.loadingText} message-size="md" state="spinning" />
        </Host>
      );
    } else if (this.results.length === 0) {
      return this.hasNoContentSlot ? (
        <Host>
          <slot name="noContentMessage" />
        </Host>
      ) : (
        <Host>
          <hp5-heading tag-selection="h3">{this.noContentText}</hp5-heading>
        </Host>
      );
    }
    return (
      <Host>
        <slot />
      </Host>
    );
  };
}
